import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import CheckIcon from '@mui/icons-material/Check';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactComponent as EllipseBackground } from '../../../icons/promotion-banner/ellipse-background.svg';
import { ReactComponent as LimitedOffer } from '../../../icons/promotion-banner/limited-offer.svg';
import { ReactComponent as Hourglass } from '../../../icons/promotion-banner/hourglass.svg';
import {
  PROMOTION_BANNER_COUNTDOWN_FORMAT_WIDTH,
  PROMOTION_BANNER_SHOW_DESC_WIDTH,
} from '../../utils';

const useStyles = makeStyles((theme => ({
  rootContainer: {
    position: 'relative',
  },
  bannerContainer: {
    width: '100%',
    height: theme.spacing(12),
    borderRadius: theme.spacing(1, 0.75, 0, 0),
    background: '#5959EB',
  },
  limitedOfferContainer: {
    display: 'inline-grid',
    width: theme.spacing(55),
  },
  countdownContainer: {
    position: 'absolute',
    justifyContent: 'center',
    left: theme.spacing(60),
    top: theme.spacing(0),
    right: theme.spacing(40),
  },
  countdownMinSize: {
    right: theme.spacing(0),
  },
  descriptionContainer: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(4),
  },
  countdownTextContainer: {
    display: 'flex',
    height: theme.spacing(12),
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: theme.spacing(3),
    lineHeight: theme.spacing(3),
  },
  ellipseBackground: {
    height: theme.spacing(12),
    position: 'absolute',
    borderRadius: theme.spacing(0.75, 0, 0, 0),
    bottom: theme.spacing(0),
    left: theme.spacing(0),
  },
  limitedOffer: {
    position: 'absolute',
    bottom: theme.spacing(0),
  },
  originalPrice: {
    position: 'absolute',
    top: theme.spacing(1.25),
    left: theme.spacing(35.6),
    lineHeight: theme.spacing(2),
    fontSize: theme.spacing(2),
    fontWeight: 400,
    textDecoration: 'line-through',
    color: '#000000',
  },
  discountPrice: {
    position: 'absolute',
    top: theme.spacing(3),
    left: theme.spacing(31.25),
    lineHeight: theme.spacing(6),
    fontSize: theme.spacing(4.5),
    fontWeight: 700,
    color: '#5959EB',
  },
  unitPrice: {
    position: 'absolute',
    top: theme.spacing(8.75),
    left: theme.spacing(34),
    lineHeight: theme.spacing(2),
    fontSize: theme.spacing(2),
    fontWeight: 400,
    color: '#000000',
  },
  countdownText: {
    width: 'auto',
  },
  hourglass: {
    marginRight: theme.spacing(1),
    minWidth: theme.spacing(3.5),
    width: theme.spacing(3.5),
    minHeight: theme.spacing(12),
    height: theme.spacing(12),
    lineHeight: theme.spacing(2),
    transform: 'rotate(20deg)',
  },
  checkIcon: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    marginRight: theme.spacing(1),
  },
  descriptionText: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 400,
    fontSize: theme.spacing(1.5),
    lineHeight: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
})));

interface PromotionCampaignBannerProps {
  currentPrice: number,
  discountPrice: number,
  priceUnit: string,
  endDate: Date,
}

enum CondownFormat {
  LONG = 'long',
  SHORT = 'short',
}
const PromotionCampaignBanner = (props: PromotionCampaignBannerProps) => {
  const classes = useStyles();
  const [showDescription, setShowDescription] = useState(true);
  const [countdownFormat, setCountdownFormat] = useState<string>(CondownFormat.LONG);

  const bannerRef = useCallback(node => {
    if (node !== null) {
      const resizeObserver = new ResizeObserver(() => {
        setShowDescription(node.clientWidth > PROMOTION_BANNER_SHOW_DESC_WIDTH);
        setCountdownFormat(
          node.clientWidth > PROMOTION_BANNER_COUNTDOWN_FORMAT_WIDTH
            ? CondownFormat.LONG
            : CondownFormat.SHORT,
        );
      });
      resizeObserver.observe(node);
    }
  }, []);

  const intl = useIntl();
  const {
    currentPrice,
    discountPrice,
    priceUnit,
    endDate,
  } = props;
  const [countdownText, setCountdownText] = useState('');

  dayjs.extend(utc);
  dayjs.extend(timezone);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = dayjs().utc().format();

      const dayDiff = dayjs(endDate).diff(Date.now(), 'd');
      const hourDiff = dayjs(endDate).diff(now, 'h');
      const minDiff = dayjs(endDate).diff(now, 'm');
      const secondDiff = dayjs(endDate).diff(now, 's');

      if (dayDiff <= 0) {
        if (secondDiff <= 0) {
          clearInterval(interval);
        } else {
          setCountdownText(intl.formatMessage(
            { id: `promotion.campaign.countdown.second.${countdownFormat}` },
            {
              h: hourDiff % 60,
              m: minDiff % 60 % 60,
              s: secondDiff % 60,
            },
          ));
        }
      } else {
        setCountdownText(intl.formatMessage(
          { id: `promotion.campaign.countdown.min.${countdownFormat}` },
          {
            d: dayDiff,
            h: hourDiff % 60 % 24,
            m: minDiff % 60,
          },
        ));
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [endDate, countdownFormat]);

  const renderLimitedOffer = () => (
    <>
      <EllipseBackground className={classes.ellipseBackground} />
      <LimitedOffer className={classes.limitedOffer} />
      <Typography variant="h4" color="white" className={classes.originalPrice}>
        <FormattedMessage
          id="promotion.campaign.banner.price"
          values={{ price: intl.formatNumber(currentPrice) }}
        />
      </Typography>
      <Typography variant="h4" color="white" className={classes.discountPrice}>
        <FormattedMessage
          id="promotion.campaign.banner.price"
          values={{ price: intl.formatNumber(discountPrice) }}
        />
      </Typography>
      <Typography variant="h4" color="white" className={classes.unitPrice}>
        <FormattedMessage id={priceUnit} />
      </Typography>
    </>
  );

  const renderCountDownComponent = () => (
    <>
      <Typography variant="h4" color="white" className={classes.countdownTextContainer}>
        <span className={classes.countdownText}>
          {countdownText}
        </span>
        <Hourglass className={classes.hourglass} />
      </Typography>
    </>
  );

  const renderDescriptionComponent = () => (
    <>
      <Typography variant="body1" color="white" className={classes.descriptionText}>
        <CheckIcon className={classes.checkIcon} />
        <FormattedMessage id="promotion.campaign.banner.first.description" />
      </Typography>
      <Typography variant="body1" color="white" className={classes.descriptionText}>
        <CheckIcon className={classes.checkIcon} />
        <FormattedMessage id="promotion.campaign.banner.second.description" />
      </Typography>
      <Typography variant="body1" color="white" className={classes.descriptionText}>
        <CheckIcon className={classes.checkIcon} />
        <FormattedMessage id="promotion.campaign.banner.third.description" />
      </Typography>
    </>
  );

  return (
    <div ref={bannerRef} id="promotion-campaign-banner" className={classes.rootContainer}>
      <div className={classes.bannerContainer}>
        <div className={classes.limitedOfferContainer}>
          {renderLimitedOffer()}
        </div>
        <div className={showDescription ? classes.countdownContainer : `${classes.countdownContainer} ${classes.countdownMinSize}`}>
          {renderCountDownComponent()}
        </div>
        {
          showDescription && (
            <div className={classes.descriptionContainer}>
              {renderDescriptionComponent()}
            </div>
          )
        }
      </div>
    </div>
  );
};

export default PromotionCampaignBanner;
